import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import classes from "./SDGGoals.module.css";
import { getSDGIcon } from "./SDGIcons";
import { BaseURL } from "../home/BaseData";

const SDG_COLORS = {
  1: "#FF7A00",
  2: "#EAB308",
  3: "#22C55E",
  4: "#EF4444",
  5: "#EC4899",
  6: "#3B82F6",
  7: "#F97316",
  8: "#06B6D4",
  9: "#64748B",
  10: "#F59E0B",
  11: "#8B5CF6",
  12: "#84CC16",
  13: "#64748B",
  14: "#0284C7",
  15: "#10B981",
  16: "#F43F5E",
  17: "#14B8A6",
};

const SDGGoals = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [sdgInfo, setSdgInfo] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSDGData = async () => {
      try {
        const urlsToTry = [
          `${BaseURL.replace(/\/$/, '')}/api/sdg/`,
          "http://127.0.0.1:8001/api/sdg/",
          "http://127.0.0.1:8000/api/sdg/",
          "http://localhost:8000/api/sdg/",
          "http://localhost:8001/api/sdg/"
        ];
        let response = null;
        for (const url of urlsToTry) {
          try {
            response = await axios.get(url);
            if (response && response.data && response.data.goals) break;
          } catch (e) {
            // try next
          }
        }

        if (response && response.data) {
          setSdgInfo(response.data.info);
          setGoals(response.data.goals || []);
        }
      } catch (error) {
        console.error("Error fetching SDG data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSDGData();
  }, []);

  const lang = i18n.language || "uz";

  const getLocalizedField = (obj, fieldPrefix) => {
    if (!obj) return "";
    return obj[`${fieldPrefix}_${lang}`] || "";
  };

  const getTitle = () => {
    const val = getLocalizedField(sdgInfo, "title");
    if (val) return val;
    if (lang === "ru") return "Цели устойчивого развития (ЦУР)";
    if (lang === "en") return "Sustainable Development Goals (SDGs)";
    return sdgInfo?.title_uz || "Barqaror Rivojlanish Maqsadlari (SDG)";
  };

  const getInfoTitle = () => {
    const val = getLocalizedField(sdgInfo, "info_title");
    if (val) return val;
    if (lang === "ru") return "Что такое ЦУР?";
    if (lang === "en") return "What are the SDGs?";
    return sdgInfo?.info_title_uz || "SDG nima ?";
  };

  const getInfoDescription = () => {
    const val = getLocalizedField(sdgInfo, "info_description");
    if (val) return val;
    if (lang === "ru") {
      return "ЦУР (Цели устойчивого развития) — это набор из 17 глобальных целей, принятых Организацией Объединённых Наций (ООН) в 2015 году. Они направлены на обеспечение экологической, экономической и социальной устойчивости в мире до 2030 года.";
    }
    if (lang === "en") {
      return "The SDGs (Sustainable Development Goals) are a set of 17 global goals adopted by the United Nations (UN) in 2015. They aim to ensure environmental, economic, and social sustainability around the world by 2030.";
    }
    return (
      sdgInfo?.info_description_uz ||
      "SDG (Sustainable Development Goals) — bu 'barqaror rivojlanish maqsadlari' bo'lib, 2015-yilda BMT (Birlashgan Millatlar Tashkiloti) tomonidan qabul qilingan 17 ta global maqsadlar to'plamidir. Ular 2030-yilgacha dunyodagi atrof-muhit, iqtisodiyot va ijtimoiy barqarorlikni ta'minlashga qaratilgan."
    );
  };

  const getGoalsHeader = () => {
    const val = getLocalizedField(sdgInfo, "goals_header");
    if (val) return val;
    if (lang === "ru") return "Цели в области устойчивого развития включают в себя";
    if (lang === "en") return "Sustainable Development Goals include";
    return sdgInfo?.goals_header_uz || "Barqaror rivojlanish maqsadlari quyidagilarni o'z ichiga oladi";
  };

  if (loading) {
    return <div className={classes.loading}>Yuklanmoqda...</div>;
  }

  return (
    <div className={classes.container}>
      {/* Header Row */}
      <div className={classes.headerRow}>
        <div className={classes.headerLeft}>
          <h1 className={classes.title}>{getTitle()}</h1>
        </div>
      </div>

      {/* Info Section */}
      <div className={classes.infoSection}>
        <div className={classes.infoTitleRow}>
          <span>{getInfoTitle()}</span>
        </div>
        <div className={classes.infoBox}>
          <p>{getInfoDescription()}</p>
        </div>
      </div>

      {/* Goals Grid Section */}
      {goals.length > 0 && (
        <>
          <h2 className={classes.sectionHeaderTitle}>{getGoalsHeader()}</h2>
          <div className={classes.goalsWrapper}>
            <div className={classes.goalsGrid}>
              {goals.map((goal) => {
                const goalTitle = getLocalizedField(goal, "title");
                const badgeColor = goal.color || SDG_COLORS[goal.number] || "#E5243B";
                const imageUrl = goal.icon
                  ? goal.icon.startsWith("http")
                    ? goal.icon
                    : `${BaseURL.replace(/\/$/, "")}${goal.icon.startsWith("/") ? "" : "/"}${goal.icon}`
                  : null;

                return (
                  <div
                    key={goal.id || goal.number}
                    className={classes.goalCard}
                    onClick={() => navigate(`/sdg/${goal.number || goal.slug}`)}
                  >
                    <div className={classes.circleWrapper}>
                      <div className={classes.circleBg} style={{ backgroundColor: `${badgeColor}25` }}>
                        {imageUrl ? (
                          <img src={imageUrl} alt={goalTitle} className={classes.circleImg} />
                        ) : (
                          getSDGIcon(goal.number, badgeColor)
                        )}
                      </div>
                      <div
                        className={classes.numberBadge}
                        style={{ backgroundColor: badgeColor }}
                      >
                        {goal.number}
                      </div>
                    </div>
                    <h3 className={classes.cardTitle}>{goalTitle}</h3>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SDGGoals;
